import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Home,
  User,
  Download,
  MessageCircle,
  Menu,
  X,
  Linkedin,
  Play,
  Dribbble,
} from "lucide-react";

const BehanceIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <text x="1" y="17" fontSize="11" fontWeight="700" fontFamily="sans-serif">
      Bē
    </text>
  </svg>
);

const CONTACT_URL =
  "https://mail.google.com/mail/?view=cm&fs=1&to=saidtomehedi@gmail.com";

export const navItems = [
  { name: "Home", href: "/", icon: Home, testId: "nav-work" },
  { name: "About", href: "/about", icon: User, testId: "nav-about" },
  { name: "Resume", href: "/Mehedi-Hassain-Resume.pdf", icon: Download, testId: "nav-resume", download: true },
  { name: "Contact", href: CONTACT_URL, icon: MessageCircle, testId: "nav-contact", external: true },
];

export const socials = [
  { name: "LinkedIn", href: "https://www.linkedin.com/in/mehedihassain", icon: Linkedin },
  { name: "Play Store", href: "https://play.google.com/store/apps/dev?id=8274750172355497627", icon: Play },
  { name: "Behance", href: "https://www.behance.net/mehedihassain2", icon: BehanceIcon },
  { name: "Dribbble", href: "https://dribbble.com/MehediHassain", icon: Dribbble },
];

const linkClass = (active) =>
  `flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium transition-all duration-200 ${
    active
      ? "bg-primary/10 text-primary shadow-sm border border-primary/20"
      : "text-muted-foreground hover:text-foreground hover:bg-muted border border-transparent"
  }`;

const NavLinks = ({ onNavigate }) => {
  const location = useLocation();
  return (
    <nav className="flex-1 px-4 space-y-2" data-testid="sidebar-nav">
      {navItems.map((item) => {
        const Icon = item.icon;
        if (item.download || item.external) {
          return (
            <a
              key={item.name}
              href={item.href}
              download={item.download ? "" : undefined}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onNavigate}
              data-testid={item.testId}
              className={linkClass(false)}
            >
              <Icon className="h-5 w-5" />
              {item.name}
            </a>
          );
        }
        const active =
          item.href === "/"
            ? location.pathname === "/" || location.pathname.startsWith("/projects")
            : location.pathname.startsWith(item.href);
        return (
          <NavLink
            key={item.name}
            to={item.href}
            onClick={onNavigate}
            data-testid={item.testId}
            className={linkClass(active)}
          >
            <Icon className="h-5 w-5" />
            {item.name}
          </NavLink>
        );
      })}
    </nav>
  );
};

const SidebarContent = ({ onNavigate }) => (
  <div className="flex flex-col h-full">
    <div className="flex flex-col items-center text-center mb-10 pt-8 px-6">
      <Link to="/" onClick={onNavigate} data-testid="sidebar-profile-link">
        <img
          src="/profile.jpg"
          alt="Mehedi Hassain"
          className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg mb-4 bg-muted"
        />
      </Link>
      <h2 className="text-xl font-semibold text-foreground mb-1">Mehedi Hassain</h2>
      <p className="text-sm text-muted-foreground">Product Designer</p>
    </div>
    <NavLinks onNavigate={onNavigate} />
    <div className="p-6 border-t border-border">
      <div className="flex items-center justify-center gap-3">
        {socials.map((s) => {
          const Icon = s.icon;
          return (
            <a
              key={s.name}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.name}
              data-testid={`sidebar-social-${s.name.toLowerCase().replace(/ /g, "-")}`}
              className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all duration-300"
            >
              <Icon className="h-4 w-4" />
            </a>
          );
        })}
      </div>
    </div>
  </div>
);

export const Sidebar = () => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <>
      <aside
        className="hidden md:block fixed left-0 top-0 z-40 h-full w-72 bg-background border-r border-border"
        data-testid="sidebar-desktop"
      >
        <SidebarContent />
      </aside>

      <header
        className="fixed top-0 left-0 right-0 z-40 md:hidden bg-background/95 backdrop-blur-sm border-b border-border"
        data-testid="mobile-header"
      >
        <div className="flex items-center justify-between p-4">
          <Link to="/" className="flex items-center space-x-3" data-testid="mobile-profile-link">
            <img
              src="/profile.jpg"
              alt="Mehedi Hassain"
              className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm bg-muted"
            />
            <div>
              <h1 className="text-sm font-semibold text-foreground">Mehedi Hassain</h1>
              <p className="text-xs text-muted-foreground">Product Designer</p>
            </div>
          </Link>
          <button
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
            className="w-10 h-10 flex items-center justify-center rounded-lg text-foreground hover:bg-muted transition-colors"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm"
          onClick={close}
          data-testid="mobile-menu-overlay"
        />
      )}
      <aside
        className={`fixed left-0 top-0 z-50 h-full w-72 bg-background border-r border-border transition-transform duration-300 ease-smooth md:hidden ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar-mobile"
      >
        <SidebarContent onNavigate={close} />
      </aside>
    </>
  );
};

export default Sidebar;
